import React, { useEffect, useMemo, useState } from "react";
import { useAppContext } from "./AppContext";
import { useNavigate, useSearchParams } from "react-router";
import { Stack } from "@fluentui/react";
import SummaryTable from "./SummaryTable";
import { IReport_ProductItem } from "../../../shared/interfaces/IReceipAnalyzerEntities";
import ActionButton from "./ActionButton";
import BrandPicker from "./BrandPicker";
import ProgressBar from './ProgressBar';
import SConsciousApiService from "../services/SConsciousApiService";
import {
    ResponsiveContainer, ComposedChart, Line, Area,
    XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

interface ProductItemProps {
  productItemId?: string;
}

const TOOLTIP_STYLE = {
    contentStyle: {
        background: '#111',
        border: '1px solid rgba(255,204,0,0.3)',
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: '0.72rem',
    },
    itemStyle: { color: '#fff' },
    labelStyle: { color: '#ffcc00', fontWeight: 700 },
} as const;

const AXIS_TICK = { fontSize: 10, fontFamily: 'inherit', fill: '#000' };

const ProductItem: React.FC<ProductItemProps> = ({ productItemId }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { appData, setAppData } = useAppContext();

  // Identify item by receiptId (partitionKey) + itemId (rowKey)
  const receiptId = searchParams.get("receiptId") ?? undefined;
  productItemId = searchParams.get("itemId") ?? undefined;

  const item = (appData?.data?.reportProductItems ?? []).find(
    (i) => i.receipt_id === receiptId && i.item_id === productItemId
  );

  const brand = appData?.data?.brands?.find((b) => b.name === item?.brand);
  const corporation = appData?.data?.corporations?.find((c) => c.name === brand?.owning_corporation);
  const [localBrand, setLocalBrand] = useState<string | undefined>(undefined);
  const [localCorporation, setLocalCorporation] = useState<string | undefined>(undefined);
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [brandRewardMessage, setBrandRewardMessage] = useState<string | null>(null);

  const displayBrand = localBrand ?? item?.brand;
  const displayCorporation = localCorporation ?? corporation?.name;

  // Fetch canonical product info
  const [canonical, setCanonical] = useState<any>(null);
  useEffect(() => {
    if (!item?.product_identifier) return;
    const api = new SConsciousApiService();
    api.getCanonicalProduct(item.product_identifier).then(c => { if (c) setCanonical(c); });
  }, [item?.product_identifier]);

  // Find all purchases of the same product (by product_identifier) across all receipts
  const allMatchingItems = (appData?.data?.reportProductItems ?? []).filter(pi => pi.product_identifier === item?.product_identifier);

  const priceChartData = useMemo(() => {
    if (allMatchingItems.length < 2) return [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const homeCurrency = appData.user.currency;
    return [...allMatchingItems]
      .filter(pi => pi.receipt_transaction_date && pi.unit_price != null)
      .sort((a, b) => (a.receipt_transaction_date ?? '').localeCompare(b.receipt_transaction_date ?? ''))
      .map(pi => {
        const d = pi.receipt_transaction_date ?? '';
        const [year, month] = d.split('-');
        const day = d.split('-')[2]?.substring(0, 2) ?? '';
        return {
          date: `${monthNames[parseInt(month) - 1]} ${day} '${year?.slice(2)}`,
          unitPrice: Math.round(pi.unit_price * 100) / 100,
          spending: Math.round((pi.total_price || 0) * 100) / 100,
          homeUnitPrice: pi.home_unit_price != null ? Math.round(pi.home_unit_price * 100) / 100 : undefined,
          currency: pi.currency_code,
          homeCurrency,
          merchant: pi.merchant_name,
        };
      });
  }, [allMatchingItems, appData.user.currency]);

  if (!appData.dataFetched) {
    return <ProgressBar label="Loading" progress={20} />;
  }

  if (!item && receiptId && productItemId) {
    navigate('/');
    return null;
  }

  return (
    <Stack horizontalAlign="center" style={{ width: '100%', margin: '0 auto' }}>
        <div className="product-item-summary">
            <br/>
            <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '0 auto', padding: '18px 0' }}>
                <div style={{ fontWeight: 700, fontSize: 16, margin: 6, letterSpacing: 1 }}>
                    {item?.description}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: 12 }}>
                    {/* Brand pill + edit button, with the edit icon offset to the right without shifting the pill's centre */}
                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ActionButton label={displayBrand || 'Unknown Brand'} onClick={() => navigate(`/Brand?brandId=${displayBrand}`)} />
                        <button
                            onClick={() => setShowBrandPicker(p => !p)}
                            title="Change brand"
                            style={{ fontFamily: 'inherit', background: 'rgba(0,0,0,0.10)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'absolute', left: '100%', marginLeft: 4 }}
                        >
                            ✎
                        </button>
                    </div>
                    {showBrandPicker && item && appData?.user?.userId && (
                        <div style={{ width: '90%', maxWidth: 340 }}>
                            <BrandPicker
                                itemId={item.item_id}
                                receiptId={item.receipt_id}
                                description={item.description}
                                merchantName={item.merchant_name ?? ''}
                                merchantCity={item.merchant_city}
                                userId={appData.user.userId}
                                initialBrand={displayBrand}
                                productIdentifier={item.product_identifier}
                                productType={item.product_type}
                                onSaved={(brand, corp) => { setLocalBrand(brand); setLocalCorporation(corp); setShowBrandPicker(false); setBrandRewardMessage('+1 sCoN earned for updating brand!'); setAppData({ ...appData, loading: true }); }}
                                onCancel={() => setShowBrandPicker(false)}
                            />
                        </div>
                    )}
                    {brandRewardMessage && !showBrandPicker && (
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#000', background: '#ffcc00', borderRadius: 8, padding: '6px 10px' }}>
                            {brandRewardMessage}
                        </div>
                    )}
                    <ActionButton label={displayCorporation || 'Unknown Corporation'} onClick={() => navigate(`/Corporation?corporationId=${displayCorporation}`)} />
                    {item?.product_type && (
                        <span
                            onClick={() => navigate(`/Category?categoryId=${encodeURIComponent(item.product_type)}`)}
                            style={{ cursor: 'pointer', background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.6)', borderRadius: 20, padding: '3px 12px', fontSize: '0.75rem', fontWeight: 600 }}
                        >
                            {item.product_type}
                        </span>
                    )}
                    {(() => {
                        const score = item?.health_score;
                        if (score === undefined || score === null) return null;
                        if (score === 0) return (
                            <span style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.4)', marginTop: 4 }}>Non-food item</span>
                        );
                        const label = score >= 7 ? '🟢' : score >= 4 ? '🟡' : '🔴';
                        const bg = score >= 7 ? '#000' : 'rgba(0,0,0,0.12)';
                        const color = score >= 7 ? '#ffcc00' : 'rgba(0,0,0,0.7)';
                        return (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color, borderRadius: 20, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700, marginTop: 4 }}>
                                {label} Health {score}/10
                            </div>
                        );
                    })()}
                </div>
                <div style={{ marginTop: 12, fontSize: 12 }}>
                    <strong>Total:</strong> {allMatchingItems.reduce((sum, pi) => sum + (pi.total_price || 0), 0).toFixed(2)} {item?.currency_code}
                </div>
            </div>
            {/* {canonical && (
                <div style={{ background: 'rgba(0,0,0,0.05)', width: '90%', borderRadius: 12, margin: '12px auto 0', padding: '12px 16px', fontSize: '0.75rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'rgba(0,0,0,0.45)', marginBottom: 8 }}>Canonical Product</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
                        <span><strong>ID:</strong> {canonical.product_identifier}</span>
                        <span><strong>Brand:</strong> {canonical.brand}</span>
                        <span><strong>Type:</strong> {canonical.product_type}</span>
                        {canonical.seen_count != null && <span><strong>Seen:</strong> {canonical.seen_count}×</span>}
                        {canonical.price_avg != null && <span><strong>Avg price:</strong> {canonical.price_avg.toFixed(2)} {canonical.currency_code || ''}</span>}
                        {canonical.last_seen && <span><strong>Last:</strong> {canonical.last_seen}</span>}
                    </div>
                    {canonical.aliases && (() => {
                        try {
                            const aliases: string[] = JSON.parse(canonical.aliases);
                            if (aliases.length > 1) return (
                                <div style={{ marginTop: 6, color: 'rgba(0,0,0,0.4)' }}>
                                    <strong>Aliases:</strong> {aliases.join(' · ')}
                                </div>
                            );
                        } catch { }
                        return null;
                    })()}
                </div>
            )} */}
            <br/>
            {priceChartData.length > 1 && (
                <div style={{ width: '90%', margin: '0 auto 12px' }}>
                    <div className="chart-card">
                        <span className="chart-title">Price Evolution</span>
                        <ResponsiveContainer width="100%" height={220}>
                            <ComposedChart data={priceChartData}>
                                <defs>
                                    <linearGradient id="piSpendGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#000" stopOpacity={0.12} />
                                        <stop offset="100%" stopColor="#000" stopOpacity={0.01} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                <XAxis dataKey="date" tick={AXIS_TICK} />
                                <YAxis tick={AXIS_TICK} domain={['auto', 'auto']} />
                                <Tooltip
                                    {...TOOLTIP_STYLE}
                                    formatter={((value: any, name: any, props: any) => {
                                        const d = props.payload;
                                        if (name === 'spending') return [`${d.currency} ${Number(value).toFixed(2)}`, 'Spending'];
                                        if (name === 'homeUnitPrice') return [`${d.homeCurrency} ${Number(value).toFixed(2)}`, 'Home Price'];
                                        return [`${d.currency} ${Number(value).toFixed(2)}`, 'Unit Price'];
                                    }) as any}
                                />
                                <Legend wrapperStyle={{ fontSize: '0.7rem', fontFamily: 'inherit' }} />
                                <Area type="monotone" dataKey="spending" stroke="#000" strokeWidth={2} fill="url(#piSpendGrad)" name="spending" connectNulls />
                                <Line type="monotone" dataKey="unitPrice" stroke="#806000" strokeWidth={2} dot={{ r: 3, fill: '#806000' }} name="unitPrice" />
                                {priceChartData.some(d => d.homeUnitPrice !== undefined) && (
                                    <Line type="monotone" dataKey="homeUnitPrice" stroke="#1a5f3c" strokeWidth={2} dot={{ r: 2, fill: '#1a5f3c' }} name="homeUnitPrice" connectNulls />
                                )}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
            <br/>
            <Stack horizontalAlign="center" style={{ margin: 'auto', width: '90%' }}>
            <SummaryTable
                data={allMatchingItems.map((pi, idx) => ({
                    id: idx,
                    ...pi,
                    transaction_date: pi.receipt_transaction_date,
                }))}
                columns={[
                { 
                    key: "merchant_name", 
                    label: "Merchant", 
                    width: "50%", 
                    total: false, 
                    render: (_: any, r: any) => (
                        <span className='table-cell-content' onClick={() => navigate(`/Receipt?receiptId=${r.receipt_id}`)} style={{ cursor: 'pointer' }}>{r.merchant_name}</span>
                    ),
                },
                { key: "transaction_date", label: "Date", width: "25%", total: false },
                { key: "quantity", label: "Qty", width: "15%", total: true, render: (v: any) => Math.round(v) },
                { key: "total_price", label: "Price", width: "35%", total: true,
                    render: (_: any, r: any) => (
                        <span className='table-cell-content'>
                            {r.total_price}
                            {r.home_total_price != null && r.home_currency_code && r.home_currency_code !== r.currency_code && (
                                <span style={{ display: 'block', fontSize: 11, opacity: 0.65 }}>
                                    ≈ {r.home_total_price.toFixed(2)} {r.home_currency_code}
                                </span>
                            )}
                        </span>
                    ),
                },
                ]}
                filterKeys={[]}
                rowsPerPage={10}
                header={true}
                total={true}
            />
        </Stack>
        </div>
    </Stack>
  );
};

export default ProductItem;